import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@wondermaps/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding } from "@fortawesome/pro-solid-svg-icons/faBuilding";
import { faFlag } from "@fortawesome/pro-solid-svg-icons/faFlag";
import { faPalette } from "@fortawesome/pro-solid-svg-icons/faPalette";
import { faChartColumn } from "@fortawesome/pro-solid-svg-icons/faChartColumn";
import { faChartMixed } from "@fortawesome/pro-solid-svg-icons/faChartMixed";
import { faScroll } from "@fortawesome/pro-solid-svg-icons/faScroll";
import { faShieldExclamation } from "@fortawesome/pro-solid-svg-icons/faShieldExclamation";
import { faHeadset } from "@fortawesome/pro-solid-svg-icons/faHeadset";
import { faEye } from "@fortawesome/pro-solid-svg-icons/faEye";
import { faBullhorn } from "@fortawesome/pro-solid-svg-icons/faBullhorn";
import { faMagnifyingGlass } from "@fortawesome/pro-solid-svg-icons/faMagnifyingGlass";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { SuperadminHeader } from "../components/superadmin/SuperadminHeader";

export const Route = createFileRoute("/superadmin/")({
  component: SuperadminDashboard,
});

type DashboardCard = {
  title: string;
  description: string;
  icon: IconDefinition;
  to: string;

};

const dashboardCards: DashboardCard[] = [
  {
    title: "Feature Flags",
    description: "Manage feature rollouts and org overrides",
    icon: faFlag,
    to: "/superadmin/feature-flags",
  },
  {
    title: "Organizations",
    description: "View and manage all organizations",
    icon: faBuilding,
    to: "/superadmin/orgs",
  },
  {
    title: "Themes",
    description: "Edit and publish map themes",
    icon: faPalette,
    to: "/superadmin/themes",
  },
  {
    title: "Support",
    description: "Handle support requests",
    icon: faHeadset,
    to: "/superadmin/support",
  },
  {
    title: "Reports",
    description: "Metrics email digest management",
    icon: faChartColumn,
    to: "/superadmin/reports",
  },
  {
    title: "Engagement Reports",
    description: "Seasonal analytics reports for client orgs",
    icon: faChartMixed,
    to: "/superadmin/engagement-reports",
  },
  {
    title: "Audit Log",
    description: "Content changes across all orgs",
    icon: faScroll,
    to: "/superadmin/audit-log",
  },
  {
    title: "Security Logs",
    description: "Auth and access events",
    icon: faShieldExclamation,
    to: "/superadmin/security-logs",
  },
  {
    title: "Accessibility",
    description: "Contrast audit and a11y scanning",
    icon: faEye,
    to: "/superadmin/a11y",
  },
  {
    title: "Promote Assets",
    description: "Seed and manage promote assets per org",
    icon: faBullhorn,
    to: "/superadmin/promote-assets",
  },
  {
    title: "Map Scanner",
    description: "Mobile readability audits for cold outreach",
    icon: faMagnifyingGlass,
    to: "/superadmin/map-scanner",
  },
];

function SuperadminDashboard() {
  const { data: supportCount } = useQuery({
    queryKey: ["support", "count"],
    queryFn: async () => {
      const res = await api.support.count.$get();
      if (!res.ok) throw new Error("Failed to fetch support count");
      return res.json() as Promise<{ open: number; inProgress: number }>;
    },
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="flex h-screen flex-col bg-background">
      <SuperadminHeader
        title="Superadmin"
        subtitle="Platform management"
        backTo="/"
      />

      {/* Content */}
      <main className="flex-1 overflow-auto p-6">
        <div className="container mx-auto max-w-4xl">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {dashboardCards.map((card) => {
              const isSupport = card.to === "/superadmin/support";
              const openCount = isSupport ? (supportCount?.open ?? 0) : 0;
              const inProgressCount = isSupport ? (supportCount?.inProgress ?? 0) : 0;
              return (
                <Link key={card.to} to={card.to} className="block">
                  <Card className="h-full transition-colors hover:bg-accent/50">
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={card.icon} className="h-5 w-5 text-muted-foreground" />
                        <CardTitle className="text-base">{card.title}</CardTitle>
                        <div className="ml-auto flex items-center gap-1.5">
                          {openCount > 0 && (
                            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1.5 text-xs font-bold text-white shadow-sm">
                              {openCount}
                            </span>
                          )}
                          {inProgressCount > 0 && (
                            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 text-xs font-bold text-white shadow-sm">
                              {inProgressCount}
                            </span>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{card.description}</p>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}
